import {
  useActiveSchedule,
  useHabitBarChartEntries,
  useHabitProgress,
  useTimeByInsightsTimeframe,
} from '@griply/hooks';
import {
  FrequencyUnit,
  Habit,
  InsightsTimeframes,
  ScheduleEntryState,
  Timestamp,
} from '@griply/types';
import { getScheduleFrequency } from '@griply/utils';
import React, { useMemo } from 'react';
import { Statistics as TaskStatistics } from 'shared/components/connected/task-detail-column/statistics';
import { TimeframeSpecifics } from 'shared/components/connected/task-detail-column/timeframe-specifics';
import { useToday } from 'shared/contexts/today';

import { useData } from '../../use-data';
import * as Styled from './task-statistics.style';

export type StatisticsProps = {
  task: Habit;
  timeframe: InsightsTimeframes;
};

export const Statistics: React.FC<StatisticsProps> = ({ task, timeframe }) => {
  const { weekStartsOn, updateHabitEntry, openManualEntryLogging } = useData();
  const today = useToday();
  const activeSchedule = useActiveSchedule(task.schedules, today);

  const { startDate, endDate } = useTimeByInsightsTimeframe(timeframe, {
    weekStartsOn,
    allMonthWeeks: activeSchedule?.frequency?.unit === FrequencyUnit.Week,
    quarterRoundedToWeeks:
      activeSchedule?.frequency?.unit === FrequencyUnit.Week,
    yearRoundedToWeeks: activeSchedule?.frequency?.unit === FrequencyUnit.Week,
    today,
  });

  const { occurrences, dateEntries, timeframeDateEntries, scheduleChanges } =
    useHabitProgress(task, {
      startDate,
      endDate,
    });

  const { entries, entriesTarget, target, completionPercentage, perfectSlots } =
    useHabitBarChartEntries(task, {
      timeframe,
      startDate,
      endDate,
      weekStartsOn,
      today,
    });

  const activeFrequency = useMemo(
    () => (activeSchedule ? getScheduleFrequency(activeSchedule) : undefined),
    [activeSchedule],
  );

  const weeklyTarget =
    activeSchedule?.frequency?.unit === FrequencyUnit.Week
      ? activeSchedule.frequency.count
      : undefined;

  const onDate = (
    date: Timestamp,
    type: ScheduleEntryState,
    options?: { value?: number; increment?: number },
  ) =>
    // if there is no type, the completion progress has to be reset
    updateHabitEntry?.(task, date, type, {
      ...(options ?? {}),
      keepDetailOpen: true,
    });

  const onManualEntryLogging = (date: Timestamp) =>
    openManualEntryLogging?.(task.id, date);

  return (
    <Styled.Container>
      <TaskStatistics
        target={target}
        completionPercentage={completionPercentage}
        dateEntries={timeframeDateEntries}
        perfectSlots={perfectSlots.length}
        slotsType={activeFrequency}
      />
      <TimeframeSpecifics
        timeframe={timeframe}
        startDate={startDate}
        endDate={endDate}
        weekStartsOn={weekStartsOn}
        entries={entries}
        entriesTarget={entriesTarget}
        occurrences={occurrences}
        dateEntries={dateEntries}
        timeframeDateEntries={timeframeDateEntries}
        target={target}
        onDate={onDate}
        onDateManual={onManualEntryLogging}
        weeklyTarget={weeklyTarget}
        monthlyTarget={
          activeSchedule?.frequency?.unit === FrequencyUnit.Month
            ? activeSchedule.frequency.count
            : undefined
        }
        showCompletionPercentage={
          ![FrequencyUnit.Week].includes(activeSchedule?.frequency?.unit!)
        }
        showSurroundingWeeks={[FrequencyUnit.Week].includes(
          activeSchedule?.frequency?.unit!,
        )}
        perfectSlots={perfectSlots}
        scheduleChanges={scheduleChanges}
      />
    </Styled.Container>
  );
};
